// Zod schemas shared between web and api.
// As CRUD endpoints are introduced (Steps 4–11), define their request/response
// schemas here so both sides reference the same shape.

import { z } from 'zod';
import type { VisibilityRule as VisibilityRuleType } from './progress.ts';

export const QuestionType = z.enum([
  'yesno',
  'text',
  'select',
  'multiselect',
  'document_adopt',
  'document_upload',
  'multiselect_uploads',
  'signature',
  'informational',
  'image_info',
]);
export type QuestionType = z.infer<typeof QuestionType>;

export const CompanyStatus = z.enum([
  'invited',
  'in_progress',
  'review',
  'changes',
  'compliant',
  'expired',
  'deactivated',
]);
export type CompanyStatus = z.infer<typeof CompanyStatus>;

export const SubmissionStatus = z.enum(['pending', 'approved', 'rejected', 'not_applicable']);
export type SubmissionStatus = z.infer<typeof SubmissionStatus>;

export const AdoptionMode = z.enum(['adopted', 'uploaded']);
export type AdoptionMode = z.infer<typeof AdoptionMode>;

export const ActorType = z.enum(['admin', 'company', 'system']);
export type ActorType = z.infer<typeof ActorType>;

// ----------------------------------------------------------------------
// Questions / adoption triggers
// ----------------------------------------------------------------------

const ConditionValue = z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]);

export const QuestionCondition = z.object({
  value: ConditionValue,
});
export type QuestionCondition = z.infer<typeof QuestionCondition>;

// Flat AND/OR visibility expression: one combinator over 1-20 leaf
// conditions. (Storage and the shared evaluator tolerate nested trees for
// legacy/out-of-band data, but the write path only accepts flat rules —
// see the spec's Revision 2026-06-06.)
const VisibilityRuleLeaf = z.object({ q: z.string().uuid(), value: ConditionValue });

export const VisibilityRule: z.ZodType<VisibilityRuleType> = z.object({
  op: z.enum(['and', 'or']),
  children: z.array(VisibilityRuleLeaf).min(1).max(20),
});
// Note: no `export type VisibilityRule` alias here — the canonical TS type
// is `VisibilityRule` from progress.ts. A duplicate alias would conflict in
// the barrel (index.ts uses `export *` over both files).

export const AdoptionTriggerInput = z.object({
  // null = unconditional adoption document (always offered / always required).
  // A non-null value is a legacy answer condition or a multiselect_uploads
  // per-option key. Normalized to null when omitted by the write path.
  trigger_value: z.string().min(1).nullable().optional(),
  document_id: z.string().uuid(),
  adopt_only: z.boolean().default(false),
});
export type AdoptionTriggerInput = z.infer<typeof AdoptionTriggerInput>;

const baseQuestionFields = {
  type: QuestionType,
  // A question belongs to a specific questionnaire AND category (migration 0032):
  // the category is a shared grouping, the questionnaire scopes the question set.
  questionnaire_id: z.string().uuid(),
  category_id: z.string().uuid(),
  label: z.string().min(1),
  help_text: z.string().nullable().optional(),
  required: z.boolean().optional(),
  requires_upload: z.boolean().optional(),
  requires_expiry: z.boolean().optional(),
  allow_multiple_files: z.boolean().optional(),
  include_in_compliance_documents: z.boolean().optional(),
  compliance_doc_label: z.string().nullable().optional(),
  exclude_from_compliance: z.boolean().optional(),
  upload_label: z.string().nullable().optional(),
  options: z.array(z.string()).nullable().optional(),
  parent_id: z.string().uuid().nullable().optional(),
  condition: QuestionCondition.nullable().optional(),
  visibility_rule: VisibilityRule.nullable().optional(),
  adoptable_doc_id: z.string().uuid().nullable().optional(),
  video_url: z.string().url().nullable().optional(),
  image_url: z.string().url().nullable().optional(),
  display_order: z.number().int().min(0).optional(),
  adoption_triggers: z.array(AdoptionTriggerInput).optional(),
};

export const QuestionCreateInput = z.object(baseQuestionFields).superRefine(crossFieldChecks);
export type QuestionCreateInput = z.infer<typeof QuestionCreateInput>;

export const QuestionUpdateInput = z
  .object({
    type: QuestionType.optional(),
    questionnaire_id: z.string().uuid().optional(),
    category_id: z.string().uuid().optional(),
    label: z.string().min(1).optional(),
    help_text: z.string().nullable().optional(),
    required: z.boolean().optional(),
    requires_upload: z.boolean().optional(),
    requires_expiry: z.boolean().optional(),
    allow_multiple_files: z.boolean().optional(),
    include_in_compliance_documents: z.boolean().optional(),
    compliance_doc_label: z.string().nullable().optional(),
    exclude_from_compliance: z.boolean().optional(),
    upload_label: z.string().nullable().optional(),
    options: z.array(z.string()).nullable().optional(),
    parent_id: z.string().uuid().nullable().optional(),
    condition: QuestionCondition.nullable().optional(),
    visibility_rule: VisibilityRule.nullable().optional(),
    adoptable_doc_id: z.string().uuid().nullable().optional(),
    video_url: z.string().url().nullable().optional(),
    image_url: z.string().url().nullable().optional(),
    display_order: z.number().int().min(0).optional(),
    adoption_triggers: z.array(AdoptionTriggerInput).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.type !== undefined) {
      crossFieldChecks(value as never, ctx);
      return;
    }
    // Partial updates skip the full cross-field checks (they can't see unset
    // fields), but combining both visibility mechanisms in ONE payload is
    // always wrong regardless of what's stored.
    visibilityExclusivityChecks(value, ctx);
  });
export type QuestionUpdateInput = z.infer<typeof QuestionUpdateInput>;

export const QuestionsReorderInput = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type QuestionsReorderInput = z.infer<typeof QuestionsReorderInput>;

// Body of PUT /api/questions/:id/option-links — reconciles which options of a
// multiselect parent show question :id as a per-option follow-up. An empty
// options array unlinks the question entirely.
export const QuestionOptionLinksInput = z.object({
  parent_id: z.string().uuid(),
  options: z.array(z.string().min(1).max(255)).max(100),
});
export type QuestionOptionLinksInput = z.infer<typeof QuestionOptionLinksInput>;

function crossFieldChecks(
  value: {
    type?: QuestionType | undefined;
    parent_id?: string | null | undefined;
    condition?: QuestionCondition | null | undefined;
    visibility_rule?: VisibilityRuleType | null | undefined;
    adoptable_doc_id?: string | null | undefined;
    options?: string[] | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  visibilityExclusivityChecks(value, ctx);
  if (value.parent_id && !value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['condition'],
      message: 'condition is required when parent_id is set',
    });
  }
  if (!value.parent_id && value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['condition'],
      message: 'condition only allowed when parent_id is set',
    });
  }
  if (
    value.adoptable_doc_id &&
    value.type !== 'document_adopt' &&
    value.type !== 'document_upload'
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adoptable_doc_id'],
      message: 'adoptable_doc_id only valid for document_adopt or document_upload',
    });
  }
  if ((value as { video_url?: string | null }).video_url && value.type !== 'informational') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['video_url'],
      message: 'video_url is only valid on informational questions',
    });
  }
  if ((value as { image_url?: string | null }).image_url && value.type !== 'image_info') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['image_url'],
      message: 'image_url is only valid on image_info questions',
    });
  }
  if (
    (value.type === 'select' ||
      value.type === 'multiselect' ||
      value.type === 'multiselect_uploads') &&
    (!value.options || value.options.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'options is required for select / multiselect questions',
    });
  }
}

function visibilityExclusivityChecks(
  value: {
    parent_id?: string | null | undefined;
    condition?: QuestionCondition | null | undefined;
    visibility_rule?: VisibilityRuleType | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.visibility_rule && value.parent_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['visibility_rule'],
      message: 'visibility_rule cannot be combined with parent_id',
    });
  }
  if (value.visibility_rule && value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['visibility_rule'],
      message: 'visibility_rule cannot be combined with condition',
    });
  }
}

// ----------------------------------------------------------------------
// Questionnaires
// ----------------------------------------------------------------------

export const QuestionnaireAudience = z.enum(['company', 'driver']);
export type QuestionnaireAudience = z.infer<typeof QuestionnaireAudience>;

// Zod schema for the questionnaire_send_mode enum (mirrors the plain-type in
// questionnaireApplies.ts; both are exported from the package index — the
// export * clash is resolved by the explicit re-export in index.ts).
const questionnaireSendModeSchema = z.enum(['on_invite', 'manual']);

export const QuestionnaireCreateInput = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  audience: QuestionnaireAudience,
  contractor_types: z.array(z.string().min(1).max(120)).nullable().optional(),
  // Which global question categories this questionnaire includes (many-to-many,
  // see migration 0031). Omitted = no change / none.
  category_ids: z.array(z.string().uuid()).optional(),
  display_order: z.number().int().min(0).optional(),
  display_as_own_tab: z.boolean().optional(),
  is_active: z.boolean().optional(),
  send_mode: questionnaireSendModeSchema.optional(),
});
export type QuestionnaireCreateInput = z.infer<typeof QuestionnaireCreateInput>;

export const QuestionnaireUpdateInput = z
  .object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    audience: QuestionnaireAudience.optional(),
    contractor_types: z.array(z.string().min(1).max(120)).nullable().optional(),
    category_ids: z.array(z.string().uuid()).optional(),
    display_order: z.number().int().min(0).optional(),
    display_as_own_tab: z.boolean().optional(),
    is_active: z.boolean().optional(),
    send_mode: questionnaireSendModeSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type QuestionnaireUpdateInput = z.infer<typeof QuestionnaireUpdateInput>;

export const QuestionnaireSendInput = z.object({ notify: z.boolean().default(true) });
export type QuestionnaireSendInput = z.infer<typeof QuestionnaireSendInput>;

// ----------------------------------------------------------------------
// Question categories
// ----------------------------------------------------------------------

// Categories are now global, standalone entities (see migration 0031). They are
// no longer created against a single questionnaire; a questionnaire ticks which
// categories it includes via `category_ids` on the questionnaire create/update.
export const QuestionCategoryCreateInput = z.object({
  name: z.string().min(1).max(255),
  display_order: z.number().int().min(0).optional(),
});
export type QuestionCategoryCreateInput = z.infer<typeof QuestionCategoryCreateInput>;

export const QuestionCategoryUpdateInput = z
  .object({
    name: z.string().min(1).max(255).optional(),
    display_order: z.number().int().min(0).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type QuestionCategoryUpdateInput = z.infer<typeof QuestionCategoryUpdateInput>;

export const QuestionCategoriesReorderInput = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type QuestionCategoriesReorderInput = z.infer<typeof QuestionCategoriesReorderInput>;

// ----------------------------------------------------------------------
// Companies
// ----------------------------------------------------------------------

const companyInfoFields = {
  contractor_types: z.array(z.string().min(1).max(255)).nullable().optional(),
  vendor_number: z.string().max(255).nullable().optional(),
  contract_numbers: z.array(z.string().min(1).max(255)).nullable().optional(),
  cluster_region: z.string().max(255).nullable().optional(),
  station: z.string().max(255).nullable().optional(),
  abn: z.string().max(255).nullable().optional(),
  trustee_info: z.string().max(1000).nullable().optional(),
  primary_contact_name: z.string().max(255).nullable().optional(),
  contact_role: z.string().max(255).nullable().optional(),
  contact_phone: z.string().max(50).nullable().optional(),
  contact_email: z.string().email().max(255).nullable().optional(),
  employee_count: z.number().int().min(0).max(10_000_000).nullable().optional(),
  legal_entity_name: z.string().max(255).nullable().optional(),
  address_line: z.string().max(500).nullable().optional(),
  city: z.string().max(255).nullable().optional(),
  state: z.string().max(255).nullable().optional(),
  postcode: z.string().max(40).nullable().optional(),
  country: z.string().max(255).nullable().optional(),
};

export const CompanyCreateInput = z
  .object({
    company_name: z.string().min(1).max(255),
    contact_name: z.string().min(1).max(255),
    email: z.string().email().max(255),
    ...companyInfoFields,
  })
  .superRefine((value, ctx) => {
    if (!value.contractor_types || value.contractor_types.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contractor_types'],
        message: 'At least one contractor type is required',
      });
    }
    if (!value.cluster_region || value.cluster_region.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cluster_region'],
        message: 'cluster_region is required',
      });
    }
    if (!value.station || value.station.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['station'],
        message: 'station is required',
      });
    }
  });
export type CompanyCreateInput = z.infer<typeof CompanyCreateInput>;

export const CompanyUpdateInput = z
  .object({
    company_name: z.string().min(1).max(255).optional(),
    contact_name: z.string().min(1).max(255).optional(),
    email: z.string().email().max(255).optional(),
    // Deactivation is managed exclusively by the deactivate/reactivate
    // endpoints (they own the snapshot column) — not settable here.
    status: CompanyStatus.exclude(['deactivated']).optional(),
    expires_at: z.string().datetime().nullable().optional(),
    ...companyInfoFields,
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type CompanyUpdateInput = z.infer<typeof CompanyUpdateInput>;

// Subset the company itself can edit through the portal — must not allow
// changing pre-filled fields (contractor_types, vendor_number, contract_numbers,
// cluster_region, station) or status / contact_name / email used for invites.
export const CompanyInfoSelfUpdateInput = z
  .object({
    abn: z.string().max(255).nullable().optional(),
    trustee_info: z.string().max(1000).nullable().optional(),
    primary_contact_name: z.string().max(255).nullable().optional(),
    contact_role: z.string().max(255).nullable().optional(),
    contact_phone: z.string().max(50).nullable().optional(),
    contact_email: z.string().email().max(255).nullable().optional(),
    employee_count: z.number().int().min(0).max(10_000_000).nullable().optional(),
    legal_entity_name: z.string().max(255).nullable().optional(),
    address_line: z.string().max(500).nullable().optional(),
    city: z.string().max(255).nullable().optional(),
    state: z.string().max(255).nullable().optional(),
    postcode: z.string().max(40).nullable().optional(),
    country: z.string().max(255).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type CompanyInfoSelfUpdateInput = z.infer<typeof CompanyInfoSelfUpdateInput>;

export const CompanyInviteInput = z.object({
  ttl_days: z.number().int().min(1).max(60).optional(),
});
export type CompanyInviteInput = z.infer<typeof CompanyInviteInput>;

// ----------------------------------------------------------------------
// Drivers (children of a company)
// ----------------------------------------------------------------------

export const DriverCreateInput = z.object({
  name: z.string().min(1).max(255),
  email: z.string().email().max(255),
  phone: z.string().max(50).nullable().optional(),
  license_number: z.string().max(120).nullable().optional(),
  license_expiry: z.string().date().nullable().optional(),
});
export type DriverCreateInput = z.infer<typeof DriverCreateInput>;

export const DriverUpdateInput = z
  .object({
    name: z.string().min(1).max(255).optional(),
    email: z.string().email().max(255).optional(),
    phone: z.string().max(50).nullable().optional(),
    license_number: z.string().max(120).nullable().optional(),
    license_expiry: z.string().date().nullable().optional(),
    // 'deactivated' exists on the shared enum for companies only.
    status: CompanyStatus.exclude(['deactivated']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type DriverUpdateInput = z.infer<typeof DriverUpdateInput>;

export const DriverInviteInput = z.object({
  ttl_days: z.number().int().min(1).max(60).optional(),
});
export type DriverInviteInput = z.infer<typeof DriverInviteInput>;

// ----------------------------------------------------------------------
// Review actions (admin)
// ----------------------------------------------------------------------

export const ReviewDecision = z.enum(['approve', 'reject', 'reset', 'na', 'comment']);
export type ReviewDecision = z.infer<typeof ReviewDecision>;

export const ReviewReferenceUpload = z.object({
  path: z.string().min(1).max(1024),
  name: z.string().min(1).max(255),
});
export type ReviewReferenceUpload = z.infer<typeof ReviewReferenceUpload>;

export const ReviewInput = z
  .object({
    decision: ReviewDecision,
    comment: z.string().min(1).max(2000).optional(),
    // Optional reference document for a change request: either adopt an existing
    // library document by id, or attach a one-off uploaded file. Mutually
    // exclusive; meaningful for 'reject' and 'comment'.
    reference_document_id: z.string().uuid().optional(),
    reference_upload: ReviewReferenceUpload.optional(),
  })
  .superRefine((value, ctx) => {
    const hasComment = !!value.comment && value.comment.trim().length > 0;
    const hasReference = !!value.reference_document_id || !!value.reference_upload;
    // Not Compliant requires SOMETHING actionable for the vendor: a comment
    // and/or a reference document.
    if (value.decision === 'reject' && !hasComment && !hasReference) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'provide a comment or a reference document when marking not compliant',
      });
    }
    // Like a reject, a standalone comment must give the vendor SOMETHING:
    // text and/or a reference document.
    if (value.decision === 'comment' && !hasComment && !hasReference) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'provide a comment or a reference document',
      });
    }
    if (value.reference_document_id && value.reference_upload) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reference_upload'],
        message: 'provide either reference_document_id or reference_upload, not both',
      });
    }
  });
export type ReviewInput = z.infer<typeof ReviewInput>;

export const BulkApproveInput = z
  .object({
    expires_in_days: z.number().int().min(1).max(3650).optional(),
  })
  .optional();
export type BulkApproveInput = z.infer<typeof BulkApproveInput>;

// ----------------------------------------------------------------------
// Submissions
// ----------------------------------------------------------------------

export const SignatureAnswer = z.object({
  signature_data_url: z.string().min(1),
  signer_name: z.string().min(1),
  signed_at: z.string().datetime(),
});
export type SignatureAnswer = z.infer<typeof SignatureAnswer>;

export const InformationalAnswer = z.object({
  watched_at: z.string().datetime(),
});
export type InformationalAnswer = z.infer<typeof InformationalAnswer>;

export const SubmissionAnswerInput = z.object({
  answer: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      SignatureAnswer,
      InformationalAnswer,
      z.null(),
    ])
    .optional(),
  comment: z.string().nullable().optional(),
});
export type SubmissionAnswerInput = z.infer<typeof SubmissionAnswerInput>;

export const SubmissionFileUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
  option_key: z.string().min(1).max(255).nullable().optional(),
});
export type SubmissionFileUploadUrlInput = z.infer<typeof SubmissionFileUploadUrlInput>;

export const SubmissionFileRegisterInput = z.object({
  file_path: z.string().min(1),
  file_name: z.string().min(1).max(255),
  size_bytes: z.number().int().min(0),
  mime_type: z.string().min(1).max(255),
  option_key: z.string().min(1).max(255).nullable().optional(),
  expires_at: z.string().datetime().nullable().optional(),
});
export type SubmissionFileRegisterInput = z.infer<typeof SubmissionFileRegisterInput>;

export const SubmissionFileUpdateInput = z
  .object({
    expires_at: z.string().datetime().nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type SubmissionFileUpdateInput = z.infer<typeof SubmissionFileUpdateInput>;

export const SubmissionAdoptionInput = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('adopted'),
    option_key: z.string().min(1).max(255).nullable().optional(),
  }),
  z.object({
    mode: z.literal('uploaded'),
    file_path: z.string().min(1),
    option_key: z.string().min(1).max(255).nullable().optional(),
  }),
]);
export type SubmissionAdoptionInput = z.infer<typeof SubmissionAdoptionInput>;

// ----------------------------------------------------------------------
// Documents
// ----------------------------------------------------------------------

export const DocumentUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
});
export type DocumentUploadUrlInput = z.infer<typeof DocumentUploadUrlInput>;

export const DocumentCreateInput = z.object({
  name: z.string().min(1),
  category: z.string().min(1),
  version: z.string().min(1),
  file_path: z.string().min(1),
  is_adoptable: z.boolean().default(true),
});
export type DocumentCreateInput = z.infer<typeof DocumentCreateInput>;

export const DocumentUpdateInput = z
  .object({
    name: z.string().min(1).optional(),
    category: z.string().min(1).optional(),
    version: z.string().min(1).optional(),
    is_adoptable: z.boolean().optional(),
    current_version: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type DocumentUpdateInput = z.infer<typeof DocumentUpdateInput>;

// ----------------------------------------------------------------------
// Portal settings & company-info labels
// ----------------------------------------------------------------------
export const CompanyInfoLabels = z
  .object({
    contractorType: z.string().min(1).max(100).optional(),
    vendorNumber: z.string().min(1).max(100).optional(),
    contractNumber: z.string().min(1).max(100).optional(),
    clusterRegion: z.string().min(1).max(100).optional(),
    station: z.string().min(1).max(100).optional(),
    primaryContact: z.string().min(1).max(100).optional(),
  })
  .optional();
export type CompanyInfoLabels = z.infer<typeof CompanyInfoLabels>;

/**
 * Admin-editable portal settings. Brand identity (colours, logo, portal name,
 * email sender) is hardcoded to Locate2u and intentionally NOT configurable.
 */
export const PortalSettings = z.object({
  welcomeHeading: z.string().optional(),
  welcomeMessage: z.string().optional(),
  companyInfoLabels: CompanyInfoLabels,
  contractorTypeOptions: z.array(z.string().min(1).max(120)).optional(),
  clusterRegionOptions: z.array(z.string().min(1).max(120)).optional(),
  stationOptions: z.array(z.string().min(1).max(120)).optional(),
  /**
   * Days-before-expiry thresholds for vendor document-expiry reminder emails,
   * e.g. [30, 20, 10]. Absent = default single 14-day reminder; explicitly
   * empty = reminders off. The cron normalizes order/duplicates.
   */
  documentExpiryReminderDays: z.array(z.number().int().min(1).max(365)).max(10).optional(),
  termsContent: z.string().optional(),
  privacyContent: z.string().optional(),
});
export type PortalSettings = z.infer<typeof PortalSettings>;

export const COMPANY_INFO_LABEL_DEFAULTS = {
  contractorType: 'Contractor Type',
  vendorNumber: 'Vendor Number',
  contractNumber: 'Contract Number',
  clusterRegion: 'Cluster Region',
  station: 'Station',
  primaryContact: 'Primary Contact',
} as const;

// ----------------------------------------------------------------------
// Submission comment threads (E: admin↔vendor per-question conversation)
// ----------------------------------------------------------------------

export const CommentAuthorType = z.enum(['admin', 'vendor']);
export type CommentAuthorType = z.infer<typeof CommentAuthorType>;

export const SubmissionCommentAttachment = z.object({
  id: z.string().uuid(),
  attachment_name: z.string(),
  /** Signed download URL resolved by the API — null if presign failed. */
  attachment_url: z.string().nullable(),
  is_answer: z.boolean(),
  attachment_expires_at: z.string().nullable(),
  replaces_file_ids: z.array(z.string().uuid()),
});
export type SubmissionCommentAttachment = z.infer<typeof SubmissionCommentAttachment>;

/** Shape returned by the API for a single thread message. */
export const SubmissionComment = z.object({
  id: z.string().uuid(),
  submission_id: z.string().uuid(),
  company_id: z.string().uuid(),
  question_id: z.string().uuid().nullable(),
  author_type: CommentAuthorType,
  author_id: z.string().uuid().nullable(),
  body: z.string().nullable(),
  attachment_name: z.string().nullable(),
  /** Signed download URL resolved by the API — null when no attachment. */
  attachment_url: z.string().nullable(),
  attachment_is_answer: z.boolean(),
  attachment_expires_at: z.string().nullable(),
  attachments: z.array(SubmissionCommentAttachment),
  created_at: z.string(),
});
export type SubmissionComment = z.infer<typeof SubmissionComment>;

/** One file attached to a reply (already uploaded to the submission-files bucket). */
export const CommentAttachmentInput = z.object({
  attachment_path: z.string().min(1).max(1024),
  attachment_name: z.string().min(1).max(255),
  attachment_size_bytes: z.number().int().min(0),
  attachment_mime_type: z.string().min(1).max(255),
  attachment_expires_at: z.string().datetime().nullable().optional(),
  /** Live submission_files ids this file supersedes; empty = additional document. */
  replaces_file_ids: z.array(z.string().uuid()).max(50).optional(),
  /** admin only: 'reference' keeps the file out of the answer; vendor sends 'replace'|'new'. */
  mode: z.enum(['reference', 'replace', 'new']).optional(),
});
export type CommentAttachmentInput = z.infer<typeof CommentAttachmentInput>;

/** Input for posting a new comment (body and/or one-or-more uploaded attachments). */
export const CommentPostInput = z
  .object({
    body: z.string().min(1).max(4000).optional(),
    attachments: z.array(CommentAttachmentInput).max(20).optional(),
  })
  .refine((v) => (v.body && v.body.trim().length > 0) || (v.attachments && v.attachments.length > 0), {
    message: 'provide a message body or at least one attachment',
  });
export type CommentPostInput = z.infer<typeof CommentPostInput>;
